import React, { useState, useEffect } from "react";
import { gql, useQuery } from "@apollo/client";
import ClassDashboardContent from "./ClassDashboardContent";
import GroupDashboardContent from "./GroupDashboardContent";
import DateSelector from "../../Components/dateSelector";
import { Select, SelectItem, Toggle } from "@carbon/react";
import { formatDateForApi, isUnitNow } from "../../Utils/timeutils";

export default function TeacherDashboard({ selectedClass }) {
    const [today, setToday] = useState(new Date());
    const [dayData, setDayData] = useState(null);
    const [selectedTimeUnit, setSelectedTimeUnit] = useState(null);
    const [autoSelectEnabled, setAutoSelectEnabled] = useState(true);

    const handleDateChange = (date, dayData) => {
        setToday(date);
        setDayData(dayData);
    };

    const GET_TIME_UNITS_QUERY = gql`
      query GetTimeUnits { 
        timeUnitsPlan { index startTime endTime }
      }
    `;

    const { data: timeUnitsData } = useQuery(GET_TIME_UNITS_QUERY);
    const timeUnits = [...(timeUnitsData?.timeUnitsPlan || [])].sort((a, b) => a.index - b.index);

    // Initialize selected time unit when time units load — but do NOT override a manual selection when auto-select is disabled
    useEffect(() => {
        if (!timeUnits || !timeUnits.length) return;
        if (selectedTimeUnit != null && !autoSelectEnabled) return; // user locked selection
        const nowUnit = timeUnits.find(u => isUnitNow(u));
        setSelectedTimeUnit(nowUnit ? nowUnit.index : timeUnits[0].index);
    }, [timeUnits, autoSelectEnabled, selectedTimeUnit]);

    // Auto-check every 5 minutes and update selected unit if current time falls into a different unit — only when autoSelectEnabled is true
    useEffect(() => {
        if (!timeUnits || !timeUnits.length) return;
        const checkAndUpdate = () => {
            if (!autoSelectEnabled) return;
            const nowUnit = timeUnits.find(u => isUnitNow(u));
            const idx = nowUnit ? nowUnit.index : (timeUnits[0] ? timeUnits[0].index : null);
            if (idx != null && idx !== selectedTimeUnit) {
                setSelectedTimeUnit(idx);
            }
        };

        // Run immediately and then every 5 minutes
        checkAndUpdate();
        const id = setInterval(checkAndUpdate, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [timeUnits, autoSelectEnabled, selectedTimeUnit]);

    const GROUP_TYPE_QUERY = gql`
      query GetGroupType($id: String!) {
        schoolGroupById(id: $id) {
          __typename
          id
          name
        }
      }
    `;

    const { data: groupData, loading, error } = useQuery(GROUP_TYPE_QUERY, {
        variables: { id: selectedClass },
        skip: !selectedClass
    });

    if (!selectedClass) {
        return (
            <section className="container mx-auto">
                <p className="text-gray-600 mb-4">
                    Bitte wählen Sie eine Klasse aus, um den Dashboard-Inhalt anzuzeigen.
                </p>
            </section>
        );
    }

    if (loading) {
        return (
            <section className="container mx-auto">
                <p className="text-gray-600 mb-4">Loading...</p>
            </section>
        );
    }

    if (error) {
        return (
            <section className="container mx-auto">
                <p className="text-red-600 mb-4">Error fetching data: {error.message}</p>
            </section>
        );
    }

    const group = groupData?.schoolGroupById;

    return (
        <section className="container mx-auto">
            <div className="my-12">
                <div className="mb-4 flex flex-col md:flex-row md:items-start md:space-x-4">
                    <DateSelector onDateChange={handleDateChange} />
                    <Select
                        id="timeUnitSelect"
                        labelText="Zeitfenster"
                        value={selectedTimeUnit != null ? String(selectedTimeUnit) : ""}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setSelectedTimeUnit(val);
                            // manual change -> disable auto-select until user re-enables
                            setAutoSelectEnabled(false);
                        }}
                    >
                        {timeUnits.map((u) => (
                            <SelectItem key={u.index} value={String(u.index)} text={`${u.startTime} - ${u.endTime}`} />
                        ))}
                    </Select>
                    <div className="flex items-center">
                        <Toggle
                            id="autoSelectToggle"
                            labelText="Automatisch umschalten"
                            labelA="Aus"
                            labelB="Ein"
                            toggled={autoSelectEnabled}
                            onToggle={(checked) => setAutoSelectEnabled(checked)}
                        />
                    </div>
                </div>
                {group?.__typename === "SchoolClass" ? (
                    <ClassDashboardContent selectedClass={selectedClass} selectedDate={today} selectedTimeUnit={selectedTimeUnit} autoSelectEnabled={autoSelectEnabled} />
                ) : group?.__typename === "Group" ? (
                    <GroupDashboardContent selectedGroup={selectedClass} selectedDate={today} selectedTimeUnit={selectedTimeUnit} autoSelectEnabled={autoSelectEnabled} />
                ) : (
                    <p className="text-gray-600 mb-4">Unbekannter Gruppentyp.</p>
                )}
            </div>
        </section>
    );
}
